import React, {
  useState,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import ParameterFields from './ParameterFields';
import Button from 'shared/components/button/Button';
import Dialog from 'components/Dialogs/Dialog';
import ConfirmLeaveModal from 'shared/components/confirm-leave-modal/ConfirmLeaveModal';
import useStore from '../../hooks/useCampaignStore';
import {
  defaultDayWeekRows,
  defaultDayPartRow,
  defaultAvailsExcludes,
  ExcludeFieldType,
  includeFieldRows,
} from '../../CampaignConstants';
import { useCallbackPrompt } from 'components/_reusable/blocker/UsePromptComponent.ts';
import { saveSpendByDayAndWeek } from '../../requests';
import { OpButton } from 'op2mise-react-widgets';
import { sortBy } from 'lodash';
import {
  dayRangesOverlap,
  timeRangesOverlap,
  validateCustomTimePeriod,
} from 'components/campaigns/helpers/helpers';
import { DAYS_OF_WEEK } from 'enums/DaysOfWeekEnum';
import { getLastIdFromArray } from 'components/campaigns/helpers/helpers';
import { CUSTOM_DAYPART_ID } from 'components/campaigns/constants';

const ParameterForm = forwardRef(({}, ref) => {
  // Campaign Store
  const {
    isCampaignActive,
    setShowCampaignForms,
    isFormTouched,
    selectedCampaign,
    setChangeFormTab,
    setIsFormSaved,
    setIsFormTouched,
    setNewCampaign,
    setSelectedCampaign,
    newCampaign,
    dayAndWeekDistributionResults,
    excludedAvailResults,
    setExcludedAvailResults,
    bonusSpotsResults,
    setBonusSpotsResults,
    includeClientNameInOrderAndTrafficResult,
    setIncludeClientNameInOrderAndTrafficResult,
    setDayAndWeekDistributionResults,
    campaignsCreateFields,
    setActiveFormId,
  } = useStore((state) => state);
  const radioType = 2;
  const isCampaignTypeRadio = selectedCampaign?.type === radioType;
  const { tvId, radioId } = CUSTOM_DAYPART_ID;
  const customDaypartId = isCampaignTypeRadio ? radioId : tvId;

  // Refs
  const scrollContainerRef = useRef(null);

  // State Declarations
  const [dayWeekList, setDayWeekList] = useState([]);
  const [dayPartList, setDayPartList] = useState([]);
  const [bookingPeriodList, setBookingPeriodList] = useState([]);
  const [dayWeekRows, setDayWeekRows] = useState(defaultDayWeekRows);
  const [dayPartRows, setDayPartRows] = useState();
  const [includeAvails, setIncludeAvails] = useState(() =>
    Object.fromEntries(includeFieldRows.map((row) => [row.name, false]))
  );
  const [validDaypartFields, setValidDaypartFields] = useState({});
  const [limits, setLimits] = useState({
    maxSpotsPerProgram: null,
    maxSpotsPerProgramPerWeek: null,
  });
  const [bonusSpots, setBonusSpots] = useState({ automaticBonusSpots: null });
  const [includeClientNameInOrderAndTraffic, setIncludeClientNameInOrderAndTraffic] = useState(true);
  const [bookingPeriods, setBookingPeriods] = useState({
    allocation: null,
    bookingTimePeriodId: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [bookingPeriodErrors, setBookingPeriodErrors] = useState({});
  const [dayWeekErrors, setDayWeekErrors] = useState({});
  const [dayPartErrors, setDayPartErrors] = useState({});
  const [limitErrors, setLimitErrors] = useState({});
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [isParametersSaving, setIsParametersSaving] = useState(false);

  const defaultAvailsExcludesValue = [
    {
      id: 1,
      type: ExcludeFieldType.RATINGS,
      selectedExclude: defaultAvailsExcludes[0].id,
      value: null,
    },
    {
      id: 2,
      type: ExcludeFieldType.RATINGS,
      selectedExclude: defaultAvailsExcludes[1].id,
      value: null,
    },
    {
      id: 3,
      type: ExcludeFieldType.UNIT_PRICE,
      selectedExclude: defaultAvailsExcludes[2].id,
      value: null,
    },
    {
      id: 4,
      type: ExcludeFieldType.UNIT_PRICE,
      selectedExclude: defaultAvailsExcludes[3].id,
      value: null,
    },
    {
      id: 5,
      type: ExcludeFieldType.CPP,
      selectedExclude: defaultAvailsExcludes[4].id,
      value: null,
    },
    {
      id: 6,
      type: ExcludeFieldType.CPP,
      selectedExclude: defaultAvailsExcludes[5].id,
      value: null,
    },
  ];

  const [availsExcludes, setAvailsExcludes] = useState(
    defaultAvailsExcludesValue
  );

  // Dirty Forms
  const [showConfirmLeaveModal, setShowConfirmLeaveModal] = useState(false);
  const [
    showPrompt,
    confirmNavigation,
    cancelNavigation,
    handleHideNavigation,
  ] = useCallbackPrompt(isFormTouched);
  const [
    shouldExecuteSaveAndProceed,
    setShouldExecuteSaveAndProceed,
  ] = useState([false, () => {}]);
  const [proceedToNavigation, setProceedToNavigation] = useState(false);

  useImperativeHandle(ref, () => ({
    submitForm(callbackFunction) {
      setShouldExecuteSaveAndProceed([true, callbackFunction]);
    },
  }));

  const handleScrollFormToBottom = () => {
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer) {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'auto',
      });
    }
  };

  const formatCustomDayRange = (startDayId, endDayId) => {
    // Create a quick lookup by id → code
    const dayMap = Object.values(DAYS_OF_WEEK).reduce((acc, day) => {
      acc[day.id] = day.code;
      return acc;
    }, {});

    if (startDayId != null && endDayId != null) {
      if (startDayId === endDayId) return dayMap[startDayId];
      else return `${dayMap[startDayId]}-${dayMap[endDayId]}`;
    }

    if (startDayId != null) {
      return dayMap[startDayId];
    }

    if (endDayId != null) {
      return dayMap[endDayId];
    }

    return '';
  };

  // Utility to check if all or none of the values are filled
  const areAllOrNoneFilled = (...values) => {
    const allFilled = values.every(Boolean);
    const allEmpty = values.every(v => !v);
    return allFilled || allEmpty;
  };

  const validateBookingPeriods = useCallback(() => {
    const bookingErrors = {};

    if (
      bookingPeriods.bookingTimePeriodId === null &&
      bookingPeriods.allocation !== null
    ) {
      bookingErrors['bookingTimePeriodError'] = 'Time period is required';
    }

    return bookingErrors;
  }, [bookingPeriods]);

  const validateDayWeekRows = useCallback(() => {
    const dayWeekErrors = {};

    // Check if total allocation exceeds to 100
    const totalDoWAllocation = dayWeekRows.reduce((sum, row) => {
      return parseFloat(sum) + (parseFloat(row.allocation) || 0);
    }, 0);

    const lastRowIndex = dayWeekRows.length - 1;
    if (totalDoWAllocation > 100)
      dayWeekErrors[dayWeekRows[lastRowIndex].id] = {
        startDay: 'Total days of week allocation cannot exceed to 100',
      };

    const sortedRows = [...dayWeekRows].sort(
      (a, b) => parseInt(a.startDayId) - parseInt(b.startDayId)
    );

    // Check for overlaps and assign errors
    sortedRows.forEach((currentRow, index) => {
      // Skip rows without start/end days
      if (!currentRow.startDayId || !currentRow.endDayId) return;

      sortedRows.forEach((compareRow, compareIndex) => {
        // Avoid comparing a row with itself
        if (index === compareIndex) return;

        const hasNullEntries = [
          compareRow.startDayId,
          compareRow.endDayId,
          currentRow.startDayId,
          currentRow.endDayId,
        ].every((value) => value != null);

        // Check for overlap
        const overlap =
          hasNullEntries &&
          compareRow.startDayId <= currentRow.endDayId &&
          compareRow.endDayId >= currentRow.startDayId;

        // If overlap exists, mark both rows with an error
        if (overlap) {
          dayWeekErrors[currentRow.id] = {
            startDay: 'Overlapping days are not allowed',
          };
          dayWeekErrors[compareRow.id] = {
            startDay: 'Overlapping days are not allowed',
          };
        }
      });
    });

    // Check if row has allocation but no defined dates
    dayWeekRows.forEach((row) => {
      if (
        !dayWeekErrors[row.id] &&
        (!row.startDayId || !row.endDayId) &&
        (row.startDayId || row.endDayId || row.allocation > 0)
      ) {
        dayWeekErrors[row.id] = {
          startDay: !row.startDayId
            ? 'Start Day is required for this field'
            : undefined,
          endDay: !row.endDayId
            ? 'End Day is required for this field'
            : undefined,
        };
      }
    });

    return dayWeekErrors;
  }, [dayWeekRows]);

  const validateDayPartRows = useCallback(() => {
    const errors = {};
    const seenDayparts = new Map(); // To track the first occurrence of each daypart
    const customTimeRanges = []; // Track rows with custom time ranges

    if (!dayPartRows.length) return errors;

    // Check if total allocation exceeds to 100
    const totalDaypartAllocation = dayPartRows.reduce((sum, row) => {
      return parseInt(sum) + (parseInt(row.allocation) || 0);
    }, 0);

    // Only get the last row of custom dayparts
    const lastRowIndex =
      dayPartRows.filter((dpr) => dpr.daypartId === customDaypartId).length - 1;
    if (totalDaypartAllocation > 100)
      errors[
        dayPartRows.filter((dpr) => dpr.daypartId === customDaypartId)[
          lastRowIndex
        ].id
      ] = {
        daypart: 'Total daypart allocation cannot exceed to 100',
      };

    dayPartRows.forEach((row) => {
      // daypartId represents a selected value in the daypart dropdown
      const {
        allocation,
        customDaypartName,
        daypartId,
        endDayId,
        endTime,
        id,
        startDayId,
        startTime,
      } = row;
      const isCustomDaypart = daypartId === customDaypartId;
      const rowValues = `${customDaypartName}-${startTime}-${endTime}-${startDayId}-${endDayId}`;

      if (daypartId && customDaypartName) {
        // Check if daypart has already been encountered
        if (
          isCustomDaypart &&
          seenDayparts.has(rowValues.toLocaleLowerCase())
        ) {
          const firstName = seenDayparts.get(rowValues);
          errors[firstName] = {
            daypart: 'Duplicate daypart names with the same time and day range are not allowed',
          };
          errors[id] = { daypart: 'Duplicate daypart names with the same time and day range are not allowed' };
        } else if (
          dayPartList.some(
            (dpl) =>
              dpl.name.toLocaleLowerCase() ===
              customDaypartName.toLocaleLowerCase()
          )
        ) {
          errors[id] = { daypart: 'Duplicate daypart names with the same time and day range are not allowed' };
        } else if (customDaypartName.toLowerCase() == "bonus") {
          errors[id] = { daypart: `"Bonus" is a reserved daypart name. Please choose a different name` };
        } else {
          // Store the first occurrence of the daypart
          seenDayparts.set(rowValues.toLocaleLowerCase(), id);
        }
      }

      const isDaypartComplete = areAllOrNoneFilled(startDayId, endDayId, startTime, endTime);
      const isEmpty = !customDaypartName && !isDaypartComplete;
      if (!errors[id] && isCustomDaypart && isEmpty) {
        errors[id] = { daypart: 'Please provide a Name, or specify the complete start and end date/time range for this field' };
      } else if (!errors[id] && isCustomDaypart && !isDaypartComplete) {
        errors[id] = { daypart: 'Please complete start and end date/time range for this field' };
      }

      // Check incomplete daypart input values for example 3:MM AM
      const isStartTimeIncomplete = validDaypartFields[id] && validDaypartFields[id].startTime === false;
      const isEndTimeIncomplete = validDaypartFields[id] && validDaypartFields[id].endTime === false;
      const isTimeRangeIncomplete = isStartTimeIncomplete && isEndTimeIncomplete;

      if (!errors[id]) {
        if (isTimeRangeIncomplete) {
          errors[id] = { daypart: 'Please complete start and end time input for this field' };
        } else if (isStartTimeIncomplete) {
          errors[id] = { daypart: 'Please complete start time input for this field' };
        } else if (isEndTimeIncomplete) {
          errors[id] = { daypart: 'Please complete end time input for this field' };
        }
      }

      // Validate that the start day is before the end day
      if (isCustomDaypart && startDayId && endDayId && startDayId > endDayId) {
        errors[id] = { daypart: 'Start day must come before end day' };
      }

      if (isCustomDaypart && startTime && endTime) {
        // Helper to convert time string (e.g., "5:00 PM") to a comparable number.
        const timeToMinutes = (timeStr) => {
          const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
          if (!match) {
            // Fallback for 24hr format
            const [h, m] = timeStr.split(':').map(Number);
            return !isNaN(h) && !isNaN(m) ? h * 60 + m : NaN;
          }
          let [, hours, minutes, modifier] = match;
          hours = parseInt(hours, 10);
          minutes = parseInt(minutes, 10);
          if (modifier.toUpperCase() === 'PM' && hours < 12) hours += 12;
          if (modifier.toUpperCase() === 'AM' && hours === 12) hours = 0; // 12 AM is 00:00
          return hours * 60 + minutes;
        };

        const startMinutes = timeToMinutes(startTime);
        const endMinutes = timeToMinutes(endTime);

        if (
          !isNaN(startMinutes) &&
          !isNaN(endMinutes) &&
          startMinutes >= endMinutes
        ) {
          errors[id] = { daypart: 'Start time must be before end time' };
        }

        const timePeriodErrors = validateCustomTimePeriod(startTime, endTime);
        const hasErrors = Object.keys(timePeriodErrors).length;

        if (hasErrors) {
          errors[id] = {
            ...(errors[id] || {}),
            ...timePeriodErrors,
          };
        }

        // Only track custom time row if it does not have errors to perform overlap check
        if (!hasErrors) {
          customTimeRanges.push({
            id,
            start: startTime,
            end: endTime,
            startDayId: startDayId,
            endDayId: endDayId,
          });
        }
      }
    });

    // Validate custom time range rows for overlapping dates
    for (let i = 0; i < customTimeRanges.length; i++) {
      for (let j = i + 1; j < customTimeRanges.length; j++) {
        const firstRange = customTimeRanges[i];
        const secondRange = customTimeRanges[j];

        // TODO: Improve sequencing of errors here but should be fine for now
        if (
          timeRangesOverlap(firstRange, secondRange) &&
          dayRangesOverlap(firstRange, secondRange)
        ) {
          errors[firstRange.id] = {
            timeRange: 'Overlapping dayparts are not allowed',
          };
          errors[secondRange.id] = {
            timeRange: 'Overlapping dayparts are not allowed',
          };
        }
      }
    }

    return errors;
  }, [dayPartRows, validDaypartFields, areAllOrNoneFilled]);

  const validateLimits = useCallback(() => {
    const { maxSpotsPerProgramPerWeek, maxSpotsPerProgram } = limits;
    const limitErrors = {};

    if (maxSpotsPerProgramPerWeek > maxSpotsPerProgram) {
      limitErrors['maxSpotsPerProgramPerWeek'] =
        'Value cannot be greater than max number of spots per program';
      limitErrors['maxSpotsPerProgram'] =
        'Value cannot be lesser than max number of spots per week per program';
    }

    return limitErrors;
  }, [limits]);

  const handleOnValidate = useCallback(() => {
    const bookingErrors = validateBookingPeriods();
    const dayWeekRowErrors = validateDayWeekRows();
    const dayPartRowErrors = validateDayPartRows();
    const limitRowErrors = validateLimits();
    const formHasErrors =
      Object.keys(bookingErrors).length ||
      Object.keys(dayWeekRowErrors).length ||
      Object.keys(dayPartRowErrors).length ||
      Object.keys(limitRowErrors).length;

    setBookingPeriodErrors(bookingErrors);
    setDayWeekErrors(dayWeekRowErrors);
    setDayPartErrors(dayPartRowErrors);
    setLimitErrors(limitRowErrors);

    return Boolean(formHasErrors);
  }, [
    validateBookingPeriods,
    validateDayWeekRows,
    validateDayPartRows,
    validateLimits,
  ]);

  // Dirty Forms
  const handleNavigate = (proceed) => {
    if (!showConfirmLeaveModal && !shouldExecuteSaveAndProceed[0])
      confirmNavigation();
    else {
      if (shouldExecuteSaveAndProceed[0]) {
        shouldExecuteSaveAndProceed[1]();
        setShouldExecuteSaveAndProceed([false, () => {}]);
      } else {
        setShowConfirmLeaveModal(false);
        if (proceed) handleCloseForm(true);
      }
    }
  };

  // Form Handlers
  const handleCloseForm = (proceedFromNavigation) => {
    if (isFormTouched && !proceedFromNavigation) {
      setShowConfirmLeaveModal(true);
    } else {
      setShowCampaignForms(false);
      setShowSaveDialog(false);
      setNewCampaign(false);
      if (showConfirmLeaveModal) setShowConfirmLeaveModal(false);
    }
  };

  const handleSubmitForm = useCallback(async () => {
    const formHasErrors = handleOnValidate();

    if (formHasErrors) {
      setChangeFormTab(false);
      if (showConfirmLeaveModal) setShowConfirmLeaveModal(false);
      else cancelNavigation();
    }

    if (!formHasErrors) {
      const excludes = availsExcludes.map((row) => ({
        id: row.selectedExclude,
        type: row.type,
        value: parseFloat(row.value) || null, // parse to float before sending to API
      }));

      setIsParametersSaving(true);

      const payload = {
        dayOfWeeks: dayWeekRows
          .filter((row) => row.startDayId && row.endDayId)
          .map((row) => ({
            startDayId: row.startDayId,
            endDayId: row.endDayId,
            allocation: parseFloat(row.allocation) || 0,
            exclude: row.exclude,
          })),
        dayparts: dayPartRows
          .filter((row) => {
            const isDaytimeComplete = row.startDayId
              && row.endDayId
              && row.startTime
              && row.endTime;
            const isDaypartComplete = row.customDaypartName?.length > 0
              || (!row.customDaypartName && isDaytimeComplete);

            if (row.daypartId !== customDaypartId || isDaypartComplete)
              return row;
          })
          .map((row) => ({
            ...row,
            allocation: isNaN(parseFloat(row.allocation))
              ? null
              : parseFloat(row.allocation),
            endTime: row.endTime === '00:00' ? '1.00:00' : row.endTime, // 1.00:00 for 24:00 so backend can parse it
            customDayRange: formatCustomDayRange(row.startDayId, row.endDayId),
          })),
        excludedAvails: excludes,
        includeAvail: includeAvails,
        bookingTimePeriod: { ...bookingPeriods },
        limits,
        bonusSpots,
        includeClientNameInOrderAndTraffic,
      };
      const newBookingPeriods = {
        allocation: payload.bookingTimePeriod.allocation,
        bookingTimePeriodId: payload.bookingTimePeriod.allocation
          ? payload.bookingTimePeriod.bookingTimePeriodId
          : null,
      };

      try {
        await saveSpendByDayAndWeek(
          selectedCampaign.id,
          JSON.stringify(payload)
        );
        setDayAndWeekDistributionResults({
          ...dayAndWeekDistributionResults,
          dayOfWeekDistributionResults: payload.dayOfWeeks,
          daypartDistributionResults: payload.dayparts,
          bookingTimePeriodResults: newBookingPeriods,
          includeAvailResults: includeAvails,
        });
        setBookingPeriods(newBookingPeriods);
        setSelectedCampaign({
          ...selectedCampaign,
          maxSpotsPerProgram: payload.limits.maxSpotsPerProgram,
          maxSpotsPerProgramPerWeek: payload.limits.maxSpotsPerProgramPerWeek,
        });

        setExcludedAvailResults(payload.excludedAvails);
        setBonusSpotsResults(payload.bonusSpots);
        setIncludeClientNameInOrderAndTrafficResult(payload.includeClientNameInOrderAndTraffic);

        setIsFormSaved(true);
        setShowSaveDialog(true);
        setChangeFormTab(false);
        setIsFormTouched(false);
        if (proceedToNavigation) setNewCampaign(false);
      } catch (error) {
        console.error(error);
        handleNavigate(false);
      } finally {
        setIsParametersSaving(false);
      }
    }
  }, [
    availsExcludes,
    bookingPeriods,
    dayWeekRows,
    dayPartRows,
    includeAvails,
    limits,
    handleOnValidate,
    showConfirmLeaveModal,
  ]);

  // Dialog Handlers
  const handleCloseSaveDialog = () => {
    if (newCampaign && !proceedToNavigation) setActiveFormId('notifications');
    setShowSaveDialog(false);
    if (proceedToNavigation) handleNavigate(true);
  };

  const handleOnHide = () => {
    if (showConfirmLeaveModal) setShowConfirmLeaveModal(false);
    else cancelNavigation();
  };

  const handleOnDiscard = () => {
    if (showConfirmLeaveModal) handleCloseForm(true);
    else {
      setNewCampaign(false);
      confirmNavigation();
    }
    setIsFormTouched(false);
    setProceedToNavigation(true);
  };

  const handleOnSaveAndProceed = useCallback(() => {
    handleSubmitForm(true);
    setProceedToNavigation(true);
    handleHideNavigation();
  }, [showConfirmLeaveModal, handleSubmitForm]);

  useEffect(() => {
    if (shouldExecuteSaveAndProceed[0]) handleOnSaveAndProceed();
  }, [shouldExecuteSaveAndProceed]);

  useEffect(() => {
    if (!newCampaign) {
      const {
        dayOfWeekDistributionResults,
        daypartDistributionResults,
        daysOfWeekFields,
        daypartFields,
        bookingTimePeriods,
        bookingTimePeriodResults,
        includeAvailResults,
      } = dayAndWeekDistributionResults;
      const sortedTimePeriods = sortBy(bookingTimePeriods, [
        (item) => {
          const match = item.name.match(/\d+/);
          return match ? parseInt(match[0], 10) : Infinity;
        },
        'name',
      ]);

      // Set Dropdown Values
      setDayWeekList(daysOfWeekFields);
      setBookingPeriodList(sortedTimePeriods);
      setDayPartList(() => {
        const filtered = daypartFields?.filter(
          (item) => item.campaignType === selectedCampaign.type
        );

        if (!filtered) return [];

        const customIndex = filtered.findIndex(
          (item) => item.name === 'Custom'
        );
        if (customIndex === -1) return filtered;

        const [customItem] = filtered.splice(customIndex, 1);
        return [customItem, ...filtered];
      });

      const currentExcludedAvails = defaultAvailsExcludesValue.map(
        (item, index) => {
          return {
            ...item,
            value: excludedAvailResults[index]?.value || '',
            selectedExclude:
              excludedAvailResults[index]?.id ??
              defaultAvailsExcludesValue[index].selectedExclude,
          };
        }
      );
      setBonusSpots({automaticBonusSpots: bonusSpotsResults?.automaticBonusSpots || null})
      setIncludeClientNameInOrderAndTraffic(includeClientNameInOrderAndTrafficResult)
      setAvailsExcludes(currentExcludedAvails);
      setIncludeAvails(includeAvailResults);
      setLimits({
        maxSpotsPerProgram: selectedCampaign?.maxSpotsPerProgram || null,
        maxSpotsPerProgramPerWeek:
          selectedCampaign?.maxSpotsPerProgramPerWeek || null,
      });
      setBookingPeriods({
        bookingTimePeriodId:
          bookingTimePeriodResults?.bookingTimePeriodId || null,
        allocation: bookingTimePeriodResults?.allocation || null,
      });

      if (
        Array.isArray(dayOfWeekDistributionResults) &&
        dayOfWeekDistributionResults?.length
      ) {
        const newDayWeekRows = dayOfWeekDistributionResults.map(
          (item, index) => ({
            ...item,
            id: index,
          })
        );
        setDayWeekRows(newDayWeekRows);
      }
      if (
        Array.isArray(daypartDistributionResults) &&
        daypartDistributionResults?.length
      ) {
        const newDayPartRows = daypartDistributionResults.map(
          (item, index) => ({
            ...item,
            id: index + 1,
          })
        );
        
        const noCustomDaypart = newDayPartRows.every((row) => row.daypartId !== customDaypartId);
        if (noCustomDaypart) {
          newDayPartRows.push({
            ...defaultDayPartRow,
            id: getLastIdFromArray(newDayPartRows) + 1,
            daypartId: customDaypartId,
          });
        }

        setDayPartRows(newDayPartRows);
      } else {
        setDayPartRows([{
          ...defaultDayPartRow,
          id: 1,
          daypartId: customDaypartId,
        }]);
      }
    }

    if (newCampaign) {
      // Set Dropdown Values
      if (campaignsCreateFields?.days?.length) {
        setDayWeekList(campaignsCreateFields?.days);
      }
      if (campaignsCreateFields?.dayparts?.length) {
        setDayPartList(() => {
          const filtered = campaignsCreateFields?.dayparts?.filter(
            (item) => item.campaignType === selectedCampaign.type
          );

          if (!filtered) return [];

          const customIndex = filtered.findIndex(
            (item) => item.name === 'Custom'
          );
          if (customIndex === -1) return filtered;

          const [customItem] = filtered.splice(customIndex, 1);
          return [customItem, ...filtered];
        });
      }
      if (campaignsCreateFields?.bookingTimePeriods?.length) {
        setBookingPeriodList(campaignsCreateFields.bookingTimePeriods);
      }

      setDayPartRows([{
        ...defaultDayPartRow,
        id: 1,
        daypartId: customDaypartId,
      }]);
    }
  }, []);

  return (
    <div className="w-100 d-flex flex-column gap-2 form parameter-form">
      <div
        ref={scrollContainerRef}
        className="min-max-h-20 d-flex flex-column gap-4 campagin-form customScroll"
      >
        <ParameterFields
          availsExcludes={availsExcludes}
          bonusSpots={bonusSpots}
          bookingPeriods={bookingPeriods}
          bookingPeriodErrors={bookingPeriodErrors}
          bookingPeriodList={bookingPeriodList}
          dayPartErrors={dayPartErrors}
          dayPartList={dayPartList}
          dayPartRows={dayPartRows}
          dayWeekErrors={dayWeekErrors}
          dayWeekList={dayWeekList}
          dayWeekRows={dayWeekRows}
          formErrors={formErrors}
          handleScrollFormToBottom={handleScrollFormToBottom}
          includeAvails={includeAvails}
          includeClientNameInOrderAndTraffic={includeClientNameInOrderAndTraffic}
          isFormTouched={isFormTouched}
          limits={limits}
          limitErrors={limitErrors}
          setAvailsExcludes={setAvailsExcludes}
          setBonusSpots={setBonusSpots}
          setBookingPeriods={setBookingPeriods}
          setIncludeAvails={setIncludeAvails}
          setIncludeClientNameInOrderAndTraffic={setIncludeClientNameInOrderAndTraffic}
          setIsFormTouched={setIsFormTouched}
          setDayPartRows={setDayPartRows}
          setDayWeekRows={setDayWeekRows}
          setDayPartErrors={setDayPartErrors}
          setDayWeekErrors={setDayWeekErrors}
          setValidDaypartFields={setValidDaypartFields}
          setLimits={setLimits}
          selectedCampaign={selectedCampaign}
        />
      </div>

      <div
        className="form-btn-container align-items-center d-flex gap-4 mt-5 justify-content-end"
        style={{ width: '95%' }}
      >
        <div>
          <Button
            text="Close"
            onClick={() => handleCloseForm(false)}
            defaultBtn
          />
        </div>
        <div>
          <OpButton
            label={'Save'}
            type="submit"
            onClick={handleSubmitForm}
            disabled={!isCampaignActive}
            loading={isParametersSaving}
          />
        </div>
      </div>

      <Dialog onClose={handleCloseSaveDialog} show={showSaveDialog}>
        Campaign {selectedCampaign?.name} has been updated.
      </Dialog>

      <ConfirmLeaveModal
        show={(showPrompt || showConfirmLeaveModal) && !showSaveDialog}
        onHide={handleOnHide}
        onSave={handleOnSaveAndProceed}
        onDiscard={handleOnDiscard}
      />
    </div>
  );
});

export default ParameterForm;
